import { logger } from '@damofire/data-commons';
import { inject, Injectable } from '@angular/core';
import { Auth, User } from '@angular/fire/auth';
import { Event, EventType, GuardsCheckEnd, Router } from '@angular/router';
import { NetworkService } from '@damofire/data-angular-commons';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { FirebaseObservableInspectionService } from '../inspection/firebase-inspection-observable.service';
import { ObservableInspectionService } from '../inspection/inspection-observable.service';
import { Inspection } from '../inspection/inspection.dto';
import { Warehouse } from './warehouse.dto';
import { WarehouseService } from './warehouse.service';

const WAREHOUSE_ID_SEGMENT = 2;
const WAREHOUSE_SUB_DOMAIN = 3;
const INSPECTION_ID_SEGMENT = 4;

const activeGuardCheckEnds = (e: Event): e is GuardsCheckEnd => e.type === EventType.GuardsCheckEnd && e.shouldActivate;

@Injectable()
export class WarehouseInterceptorService {
  private readonly router = inject(Router);
  private readonly warehouseService = inject(WarehouseService);
  private readonly observableInspectionService = inject(ObservableInspectionService);
  private readonly firebaseObservableInspectionService = inject(FirebaseObservableInspectionService);
  private readonly networkService = inject(NetworkService);
  private readonly auth = inject(Auth);

  private inspectionSub: Subscription;
  private currentInspectionWarehouseId: string = null;
  private readonly currentWarehouse: BehaviorSubject<Warehouse> = new BehaviorSubject(null);
  private readonly currentInspection: BehaviorSubject<Inspection> = new BehaviorSubject(null);

  private listeningForLogout = false;
  $warehouse = this.currentWarehouse.asObservable();
  $inspection = this.currentInspection.asObservable();

  constructor() {
    this.router.events
      .pipe(filter(activeGuardCheckEnds))
      .subscribe((event: GuardsCheckEnd) => {
        if (!event.url.startsWith('/warehouses') && !event.url.startsWith('/storage')) {
          this.clearInspection();
          return;
        }

        const segments = event.url.split('/');

        const warehouseId = segments[WAREHOUSE_ID_SEGMENT];
        this.resolveWarehouse(warehouseId);

        if (segments.length > INSPECTION_ID_SEGMENT && segments[WAREHOUSE_SUB_DOMAIN] === 'inspections') {
          const inspectionId = segments[INSPECTION_ID_SEGMENT];
          this.resolveInspection(warehouseId, inspectionId, event.id === 1);
        } else {
          this.clearInspection();
        }
      });
  }

  private resolveWarehouse(warehouseId: string): void {
    if (warehouseId && warehouseId !== this.currentWarehouse.value?._id) {
      logger.info('New warehouse selection detected.', { previousId: this.currentWarehouse.value?._id, warehouseId, category: 'warehouse' });
      this.warehouseService.get(warehouseId).then((warehouse) => {
        this.currentWarehouse.next(warehouse);
        logger.info('Resolved warehouse.', { warehouseId: warehouse?._id, category: 'warehouse' });
      });
    }
  }

  private resolveInspection(warehouseId: string, inspectionId: string, firstRouterEvent: boolean): void {
    if (inspectionId && (inspectionId !== this.currentInspection.value?._id || warehouseId !== this.currentInspectionWarehouseId)) {
      this.listenForLogout();
      logger.info('New inspection selection detected.', { previousId: this.currentInspection.value?._id, inspectionId, category: 'warehouse' });
      this.inspectionSub?.unsubscribe();
      if (this.currentInspection.value) {
        this.currentInspection.next(null);
      }
      this.currentInspectionWarehouseId = warehouseId;

      let inspectionObservable = this.observableInspectionService.observeInspection(warehouseId, inspectionId);
      // If online we bypass idb otherwise we get stuck on the lockingIdbService before the sync starts on page/app reload
      if (this.networkService.connected && firstRouterEvent) {
        inspectionObservable = this.firebaseObservableInspectionService.observeInspection(warehouseId, inspectionId);
      }

      this.inspectionSub = inspectionObservable.subscribe((inspection) => {
        if (inspection.changeType !== 'removed') {
          this.currentInspection.next(inspection);
        }
      });
    }
  }

  private clearInspection(): void {
    this.inspectionSub?.unsubscribe();
    this.inspectionSub = null;
    this.currentInspectionWarehouseId = null;
    if (this.currentInspection.value) {
      this.currentInspection.next(null);
    }
  }

  private listenForLogout(): void {
    if (!this.listeningForLogout) {
      this.listeningForLogout = true;
      this.auth.onAuthStateChanged((user: User) => {
        if (!user) {
          this.inspectionSub?.unsubscribe();
        }
      });
    }
  }
}
